// ── Turnover section layouts (2026-09-23) ────────────────────────────────────
// A turnover's sections are numbered by the layout it was created with
// (Turnovers.SectionLayoutVersion, set by sp_CreateTurnover):
//   1 (NULL) — the original 18 sections; §4 Administrative Documents and
//              §16 Permits, Bonds & Other AHJ Requirements are separate.
//   2        — 17 sections; §4 "Contract Requirements" is an applicability
//              checklist that absorbed §16; Risk Register = 16, Open Action
//              Items = 17.
//   3        — layout 2 plus §18 "Marketing" (2026-09-24), and the
//              Cover Sheet's required Client confidentiality answer.
// Existing turnovers keep layout 1. Everything that depends on WHICH section
// a number is must go through here — never compare SectionNumber to 16/17/18.

const COMMON = {
  1: "cover", 2: "contract_review", 3: "executed", 5: "proposal", 6: "raci",
  7: "estimate", 8: "quotes", 9: "schedule", 10: "takeoffs", 11: "drawings",
  12: "specs", 13: "addenda", 14: "correspondence", 15: "design_cad",
};

export const LAYOUTS = {
  1: { ...COMMON, 4: "admin", 16: "permits", 17: "risk", 18: "actions" },
  2: { ...COMMON, 4: "requirements", 16: "risk", 17: "actions" },
  3: { ...COMMON, 4: "requirements", 16: "risk", 17: "actions", 18: "marketing" },
};

export function layoutOf(turnover) {
  const v = Number(turnover?.SectionLayoutVersion);
  return LAYOUTS[v] ? v : 1;
}

// Layout 3+: §18 Marketing exists and the Cover Sheet asks the Client
// confidentiality question (required to complete §1).
export const hasMarketingKickoff = (turnover) => sectionNumberOf(turnover, "marketing") != null;

export function sectionKind(turnover, sectionNumber) {
  return LAYOUTS[layoutOf(turnover)][Number(sectionNumber)] || null;
}

export function sectionNumberOf(turnover, kind) {
  const map = LAYOUTS[layoutOf(turnover)];
  const hit = Object.entries(map).find(([, k]) => k === kind);
  return hit ? Number(hit[0]) : null;
}

export function lastSectionNumber(turnover) {
  return Math.max(...Object.keys(LAYOUTS[layoutOf(turnover)]).map(Number));
}

export function sectionCount(turnover) {
  return Object.keys(LAYOUTS[layoutOf(turnover)]).length;
}

// Document types offered on upload, by section kind.
export const DOC_TYPES_BY_KIND = {
  contract_review: ["Executed Contract", "Review Summary", "Sign-Off Document", "Draft Contract", "Redline", "Other"],
  executed:        ["Executed Contract", "Letter of Intent", "Purchase Order", "Change Order", "NTP", "Other"],
  admin:           ["Tax Exempt Certificate", "Insurance Certificate", "W-9", "Bonding Document", "Prevailing Wage Determination", "Davis-Bacon Certification", "Site Safety Plan", "OSHA Compliance Document", "Owner Safety Program", "OCIP / CCIP Enrollment", "Other"],
  requirements:    ["Tax Exempt Certificate", "W-9", "Insurance Certificate", "Lien Waiver", "Prevailing Wage Determination", "Davis-Bacon Certification", "Site Safety Plan", "Owner Safety Program", "OCIP / CCIP Enrollment", "Bonding Document", "Permit", "AHJ Requirement", "Inspection Report", "OSHA Compliance Document", "Other"],
  proposal:        ["Proposal", "Scope of Work", "Clarifications", "Revision", "Other"],
  raci:            ["RACI Matrix", "Revision", "Other"],
  estimate:        ["Estimate", "Labor Summary", "Material Summary", "Equipment Summary", "Subcontract Summary", "General Conditions", "Budget Export", "Other"],
  quotes:          ["Vendor Quote", "Subcontractor Quote", "Manufacturer Quote", "Budget Quote", "Final Quote", "Other"],
  schedule:        ["Contract Schedule", "Milestone Schedule", "Lookahead", "Revision", "Other"],
  takeoffs:        ["Floor Plan", "Reflected Ceiling", "Mechanical", "Electrical", "Detail", "Revision", "Other"],
  drawings:        ["Drawing Set", "Revision", "Addendum Drawing", "Other"],
  specs:           ["Specification Book", "Section", "Revision", "Other"],
  addenda:         ["Addendum", "RFI", "RFI Response", "Supplemental", "Other"],
  correspondence:  ["Email", "Meeting Notes", "Scope Clarification", "Client Commitment", "Verbal Agreement Confirmation", "Decision Log", "Other"],
  design_cad:      ["CAD Background", "Design Criteria", "Basis of Design", "Other"],
  permits:         ["Permit", "Bond", "AHJ Requirement", "Inspection Report", "Other"],
  risk:            ["Risk Register", "Update", "Other"],
  actions:         ["Action Item Log", "Update", "Other"],
  marketing:       ["Rendering", "Site Photo", "Sign Proof", "Project Sheet", "Other"],
};

// Sections whose default owner is the Estimator (SectionRoleDefaults 7–13).
// The Estimator stays owner and primary; the turnover's salesperson may also
// upload to and complete them.
const ESTIMATOR_KINDS = new Set(["estimate", "quotes", "schedule", "takeoffs", "drawings", "specs", "addenda"]);
export function sharesWithSalesperson(turnover, sectionNumber) {
  return ESTIMATOR_KINDS.has(sectionKind(turnover, sectionNumber));
}

export function docTypesFor(turnover, sectionNumber) {
  return DOC_TYPES_BY_KIND[sectionKind(turnover, sectionNumber)] || ["Other"];
}

// Sections that often don't apply (Mark N/A prompted).
const CONDITIONAL_KINDS = new Set(["design_cad", "permits"]);
export function isConditionalSection(turnover, sectionNumber) {
  return CONDITIONAL_KINDS.has(sectionKind(turnover, sectionNumber));
}

// SharePoint subfolders provisioned for a job — "NN - Section Name", matching
// the upload path sharePoint.js builds from SectionNumber + SectionName.
const SUBFOLDER_NAMES = {
  cover: "Cover Sheet", contract_review: "Contract Review & Sign-Off", executed: "Executed Commercial Documents",
  admin: "Administrative Documents", requirements: "Contract Requirements", proposal: "Proposal - Scope of Work",
  raci: "RACI Matrix", estimate: "Project Estimate & Budget", quotes: "Vendor & Subcontractor Quotes",
  schedule: "Project Schedule", takeoffs: "CleanSpace Takeoff Drawings", drawings: "Contract Drawings",
  specs: "Specifications", addenda: "Addenda & RFIs Affecting Pricing", correspondence: "Correspondence",
  design_cad: "Design Criteria - CAD Backgrounds", permits: "Permits, Bonds & Other AHJ Requirements",
  risk: "Risk Register", actions: "Open Action Items", marketing: "Marketing",
};
export function subfolderNames(layout) {
  const map = LAYOUTS[layout] || LAYOUTS[1];
  return Object.keys(map).map(Number).sort((a, b) => a - b)
    .map(n => `${String(n).padStart(2, "0")} - ${SUBFOLDER_NAMES[map[n]]}`);
}
